"use client";
import {
  Text,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Image,
  SimpleGrid,
  Spinner,
  Box,
  Wrap,
  WrapItem
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { Navbarforhome, PromptTooltip, ImageViewerModal } from "components";
import { usePathname } from "next/navigation";
import { Metadata } from "next";
import { useDisclosure } from "@chakra-ui/react";
import { useState, useRef, useCallback, useEffect } from "react";
import { useGetGalleryImages } from "queries";
import { GalleryImage } from "types";
import { useRouter } from "next/navigation";
import { GET_USERNAME_BY_FILENAME } from "api"; // or wherever it's defined
import Masonry from "react-masonry-css";
import "../app/globals.css";

export const metadata: Metadata = {
  title: {
    absolute: "Free Public AI Models | DeepMode Character Gallery",
  },
  description:
    "Explore DeepMode's collection of free public AI models. Use these diverse characters in our AI art generator to create stunning digital art. Discover a world of AI-powered creativity accessible to all users.",
};

const ImageGallery = ({ openImageId }: { openImageId?: string }) => {
  const pathname = usePathname();
  const pageSize = 16;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
  } = useGetGalleryImages(pageSize);

  const images = data?.pages.flatMap((page) => page.gallery_images) ?? [];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [selectedCreator, setSelectedCreator] = useState<string | null>(null);
  const [hoveredImageId, setHoveredImageId] = useState<string | null>(null);
  const [usernameMap, setUsernameMap] = useState<Record<string, string>>({});
  
  const [searchText, setSearchText] = useState("");
  const [promptMap, setPromptMap] = useState<Record<string, string>>({});

  const filteredImages = images.filter((img) => {
    const prompt = promptMap[img._id] || "";
    return prompt.toLowerCase().includes(searchText.toLowerCase());
  });
  const router = useRouter();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const handleUsernameFetch = useCallback((id: string, username: string) => {
    setUsernameMap((prev) => {
      if (prev[id] !== username) {
        return { ...prev, [id]: username };
      }
      return prev;
    });
  }, []);
  
  const handlePromptFetch = useCallback((id: string, prompt: string) => {
    setPromptMap((prev) => {
      if (prev[id] !== prompt) {
        return { ...prev, [id]: prompt };
      }
      return prev;
    });
  }, []);
  

useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isLoading) {
        fetchNextPage();
      }
    },
    { threshold: 1.0 }
  );

  const node = loadMoreRef.current;
  if (node) observer.observe(node);

  return () => {
    if (node) observer.unobserve(node);
  };
}, [hasNextPage, isLoading, fetchNextPage]);

const scrollYBeforeModal = useRef(0); // 👈 store scroll position

useEffect(() => {
  if (openImageId && images.length > 0) {
    const match = images.find((img) => img._id === openImageId);
    if (match) {
      const scrollY = window.scrollY;
      scrollYBeforeModal.current = scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflowY = "scroll";
      setSelectedImage(match);
      setSelectedCreator(usernameMap[match._id] || null);
      onOpen();
    }
  }
}, [openImageId, images]);

const breakpointColumnsObj = {
  default: 4,
  1100: 3,
  700: 2,
  500: 1
};

  
  return (
    <Flex direction="column">
      <Flex
        opacity={0.9}
        h="100%"
        direction="column"
        backgroundRepeat="no-repeat"
        bg="#050918"
        position="relative"
      >
        <Navbarforhome />
      </Flex>

      <Flex
        direction="column"
        align="center"
        justify="center"
        px={{ base: "24px", md: "80px" }}
        pt={{ base: "40px", md: "80px" }}
        pb={{ base: "32px", md: "60px" }}
        bg="#050918"
        textAlign="center"
      >
        <Text
          fontSize={{ base: "32px", md: "60px" }}
          fontWeight="800"
          bgGradient="linear(to-r, #D91C5C, #702EFF)"
          bgClip="text"
        >
          Discover Amazing AI Artworks
        </Text>

        <Text
          mt="20px"
          fontSize={{ base: "18px", md: "24px" }}
          color="gray.300"
          maxW="640px"
        >
          A community-driven gallery showcasing the best AI-generated art
        </Text>

        <InputGroup
          mt="40px"
          maxW="600px"
          w="full"
          borderRadius="full"
          bg="#16181F"
          boxShadow="0 0 0 1px rgba(255,255,255,0.1)"
        >
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.500" />
          </InputLeftElement>
          <Input
              placeholder="Search by prompt keywords..."
              borderRadius="full"
              px="20px"
              py="24px"
              fontSize="16px"
              color="white"
              bg="transparent"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              _placeholder={{ color: "gray.500" }}
              _focus={{ outline: "none", border: "none" }}
            />

        </InputGroup>
      </Flex>
{/* Masonry Gallery */}
{isLoading ? (
  <Flex justify="center" align="center" minH="70vh" bg="#050918" w="100%">
    <Spinner color="white" size="xl" />
  </Flex>
) : isError ? (
  <Text color="red.500" textAlign="center" py={8}>
    Failed to load gallery images.
  </Text>
) : (
  
  <Box bg="#050918" px={{ base: "24px", md: "80px" }} pb="80px">
    <Masonry
      breakpointCols={breakpointColumnsObj}
      className="my-masonry-grid"
      columnClassName="my-masonry-grid_column"
    >
      {filteredImages.map((img: GalleryImage) => (
        <Box
          key={img._id}
          mb="24px"
          position="relative"
          role="group"
          sx={{ breakInside: "avoid" }}
          overflow="hidden"
          borderRadius="12px"
          boxShadow="0 2px 10px rgba(160, 160, 160, 0.3)"
          onMouseEnter={() => setHoveredImageId(img._id)}
        >
          <Image
            src={img.file_name.startsWith("http") ? img.file_name : img.sas_url}
            alt={`AI artwork ${img._id}`}
            objectFit="cover"
            w="100%"
            transition="transform 0.3s ease"
            _groupHover={{ transform: "scale(1.05)" }}
            borderRadius="12px"
            cursor="pointer"
            onClick={() => {
              scrollYBeforeModal.current = window.scrollY;
              document.body.style.position = "fixed";
              document.body.style.top = `-${scrollYBeforeModal.current}px`;
              document.body.style.left = "0";
              document.body.style.right = "0";
              document.body.style.overflowY = "scroll";
              setSelectedImage(img);
              const creator = (usernameMap[img._id] || "unknown")
                .replace(/\s+/g, "-")
                .replace(/[^a-zA-Z0-9-]/g, "")
                .toLowerCase();
              const prompt = promptMap[img._id] || "";
              const promptSlug = prompt
                .split(" ")
                .slice(0, 5)
                .join("-")
                .replace(/[^a-zA-Z0-9-]/g, "")
                .toLowerCase();
              const slugUrl = `/gallery/${img._id}`;
              window.history.pushState({}, "", slugUrl);
              setSelectedCreator(usernameMap[img._id] || null);
              onOpen();
            }}
          />
          <PromptTooltip
            fileName={img.file_name}
            isFlux={false}
            onUsernameFetch={(username) => handleUsernameFetch(img._id, username)}
            onPromptFetch={(prompt) => handlePromptFetch(img._id, prompt)}
          />
        </Box>
      ))}
      {hasNextPage && <Box ref={loadMoreRef} h="1px" />}
      {isFetchingNextPage && (
          <Flex justify="center" align="center" py="40px">
            <Spinner color="white" size="lg" />
          </Flex>
        )}
    </Masonry>
  </Box>
)}
     
      {selectedImage && (
        <ImageViewerModal
          isOpen={isOpen}
          onClose={() => {
            const scrollY = scrollYBeforeModal.current;

            // Revert body to normal scrolling
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.right = "";
            document.body.style.overflowY = "";

            // Allow DOM to restore before applying scroll
            requestAnimationFrame(() => {
              window.scrollTo(0, scrollY);
            });
            window.history.replaceState({}, "", "/gallery");
            setSelectedImage(null);
            setSelectedCreator(null);
            onClose();
            

          }}          
          image={selectedImage}
          preloadedUsername={selectedCreator}
      />
      )}

    </Flex>
    
  );
};

export default ImageGallery;
